import type { ZodError } from "zod";

/** Minimal form API needed to surface Zod issues on TanStack field meta. */
export interface FormFieldMetaTarget {
	getFieldMeta: (fieldName: string) => { errors?: unknown[] } | undefined;
	setFieldMeta: (
		fieldName: string,
		updater: (prev: {
			errorMap?: Record<string, unknown>;
			errorSourceMap?: Record<string, unknown>;
			[key: string]: unknown;
		}) => {
			errorMap?: Record<string, unknown>;
			errorSourceMap?: Record<string, unknown>;
			[key: string]: unknown;
		},
	) => void;
}

/**
 * Maps top-level Zod issue paths onto TanStack Form field `errorMap.onSubmit`
 * when the field does not already have errors (e.g. from field validators).
 *
 * @param form - TanStack form API with get/set field meta.
 * @param error - Zod parse error from `safeParse`.
 */
export function applyZodIssuesToFormFieldMeta(
	form: FormFieldMetaTarget,
	error: ZodError,
): void {
	const applied = new Set<string>();

	for (const issue of error.issues) {
		const fieldName = issue.path[0];
		if (typeof fieldName !== "string" || fieldName.length === 0) continue;
		if (applied.has(fieldName)) continue;

		const existing = form.getFieldMeta(fieldName);
		if (existing?.errors && existing.errors.length > 0) continue;

		applied.add(fieldName);
		form.setFieldMeta(fieldName, (prev) => ({
			...prev,
			errorMap: {
				...prev.errorMap,
				onSubmit: issue.message,
			},
			errorSourceMap: {
				...prev.errorSourceMap,
				onSubmit: "field",
			},
		}));
	}
}

/**
 * Returns true when any registered field currently has meta errors.
 */
export function formHasFieldMetaErrors(form: {
	state: { fieldMeta: Record<string, { errors?: unknown[] } | undefined> };
}): boolean {
	return Object.values(form.state.fieldMeta).some(
		(meta) => (meta?.errors?.length ?? 0) > 0,
	);
}
